import { useState } from "react";
import { toast } from "sonner";
import { Sparkles, Copy, RefreshCw, ImagePlus, Wand2, Calendar, X } from "lucide-react";
import api, { API } from "@/lib/api";

const MEDIUMS = [
  ["", "Any medium"], ["charcoal", "Charcoal"], ["watercolor", "Watercolor"],
  ["acrylic", "Acrylic"], ["photography", "Photography"], ["caricature", "Caricature"],
];
const OCCASIONS = [
  ["", "General"], ["new_piece", "New piece"], ["commission_spotlight", "Commission spotlight"],
  ["promo", "Promo"], ["referral_push", "Referral push"],
  ["behind_the_scenes", "Behind the scenes"], ["restock", "Restock"],
];
const PLATFORMS = [
  ["instagram_post", "Instagram Post"], ["instagram_story", "Instagram Story"],
  ["instagram_reel", "Instagram Reel caption"], ["facebook_post", "Facebook Post"],
  ["facebook_story", "Facebook Story"],
];
const WEEK = [
  ["Mon", "New piece reveal"], ["Tue", "Customer love / testimonial"], ["Wed", "Behind-the-scenes / process"],
  ["Thu", "Medium spotlight or tips"], ["Fri", "Promo / referral push"], ["Sat", "Commission spotlight"],
  ["Sun", "Personal / story time"],
];

export default function ContentGeneratorTab() {
  const [form, setForm] = useState({ subject: "", medium: "", occasion: "", platform: "instagram_post", include_promo: true });
  const [imageFileId, setImageFileId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [checked, setChecked] = useState({});

  const set = (k, v) => setForm({ ...form, [k]: v });

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImageFileId(r.data.id);
    } catch { toast.error("Image upload failed"); } finally { setUploading(false); }
  };

  const generate = async () => {
    setLoading(true);
    try {
      const r = await api.post("/admin/ai/marketing-content", { ...form, file_id: imageFileId });
      setResult(r.data);
    } catch { toast.error("Generation failed — try again"); } finally { setLoading(false); }
  };

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success("Copied to clipboard"); }
    catch { toast.error("Copy failed"); }
  };
  const hashLine = result?.hashtags?.join(" ") || "";

  return (
    <div data-testid="admin-content-generator">
      <h1 className="font-display font-bold text-3xl mb-1">AI Content Generator</h1>
      <p className="text-ink/60 mb-6">Generate ready-to-paste captions & hashtags for Facebook and Instagram. Review, copy, and post manually.</p>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* INPUT PANEL */}
        <div className="brut-card p-5 space-y-4">
          <div className="flex items-center gap-2 font-display font-bold text-xl"><Wand2 className="w-5 h-5 text-bubble" /> What are we posting about?</div>

          <div>
            <label className="text-sm font-semibold text-ink/70">Artwork image (optional)</label>
            {imageFileId ? (
              <div className="relative mt-1 w-40">
                <img src={`${API}/files/${imageFileId}`} alt="upload" className="w-40 h-40 object-cover brut border-ink" data-testid="cg-image-preview" />
                <button onClick={() => setImageFileId(null)} className="absolute -top-2 -right-2 bg-tangerine text-white rounded-full p-1 border-2 border-ink"><X className="w-4 h-4" /></button>
              </div>
            ) : (
              <label className="mt-1 flex items-center gap-2 brut-btn bg-white !py-2 !px-4 text-sm cursor-pointer w-fit" data-testid="cg-upload-label">
                <ImagePlus className="w-4 h-4" /> {uploading ? "Uploading…" : "Upload image"}
                <input type="file" accept="image/*" className="hidden" onChange={onUpload} data-testid="cg-image-input" />
              </label>
            )}
          </div>

          <div>
            <label className="text-sm font-semibold text-ink/70">Subject / title</label>
            <input className="brut-input mt-1" placeholder="e.g. Charcoal portrait of a golden retriever"
              value={form.subject} onChange={(e) => set("subject", e.target.value)} data-testid="cg-subject" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-semibold text-ink/70">Medium</label>
              <select className="brut-input mt-1 !py-2 text-sm" value={form.medium} onChange={(e) => set("medium", e.target.value)} data-testid="cg-medium">
                {MEDIUMS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm font-semibold text-ink/70">Occasion</label>
              <select className="brut-input mt-1 !py-2 text-sm" value={form.occasion} onChange={(e) => set("occasion", e.target.value)} data-testid="cg-occasion">
                {OCCASIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="text-sm font-semibold text-ink/70">Platform</label>
            <select className="brut-input mt-1 !py-2 text-sm" value={form.platform} onChange={(e) => set("platform", e.target.value)} data-testid="cg-platform">
              {PLATFORMS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>

          <label className="flex items-center gap-2 font-semibold text-sm">
            <input type="checkbox" checked={form.include_promo} onChange={(e) => set("include_promo", e.target.checked)} data-testid="cg-include-promo" />
            Reference my active referral / promo offer
          </label>

          <button onClick={generate} disabled={loading} className="brut-btn bg-bubble text-white w-full justify-center text-lg" data-testid="cg-generate">
            <Sparkles className="w-5 h-5" /> {loading ? "Writing your post…" : "Generate content"}
          </button>
        </div>

        {/* OUTPUT PANEL */}
        <div className="space-y-4">
          {!result && <div className="brut-card p-6 text-ink/60">Fill in a few details and hit <b>Generate</b> — you'll get 2-3 ready-to-post options.</div>}
          {result && (
            <>
              <div className="flex items-center justify-between">
                <span className="tag-pill bg-sun">{result.platform_label}{result.is_story ? " · overlay lines" : ""}</span>
                <button onClick={generate} disabled={loading} className="brut-btn !py-2 !px-4 bg-white text-sm" data-testid="cg-regenerate">
                  <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Regenerate
                </button>
              </div>

              {result.variations.map((v, i) => (
                <div key={i} className="brut-card p-4" data-testid={`cg-variation-${i}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-display font-bold text-sm text-ink/60">Option {i + 1}</span>
                    <button onClick={() => copy(result.is_story ? v : `${v}\n\n${hashLine}`)} className="brut-btn !py-1.5 !px-3 bg-sky text-xs" data-testid={`cg-copy-${i}`}>
                      <Copy className="w-3.5 h-3.5" /> Copy{result.is_story ? "" : " + tags"}
                    </button>
                  </div>
                  <p className="text-sm whitespace-pre-line">{v}</p>
                </div>
              ))}

              {hashLine && (
                <div className="brut-card p-4 bg-grape/15" data-testid="cg-hashtags">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-display font-bold text-sm text-ink/60">Hashtags</span>
                    <button onClick={() => copy(hashLine)} className="brut-btn !py-1.5 !px-3 bg-white text-xs" data-testid="cg-copy-hashtags">
                      <Copy className="w-3.5 h-3.5" /> Copy hashtags
                    </button>
                  </div>
                  <p className="text-sm text-ink/80 break-words">{hashLine}</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* WEEKLY IDEAS */}
      <div className="brut-card p-5 mt-8">
        <div className="flex items-center gap-2 font-display font-bold text-xl mb-1"><Calendar className="w-5 h-5 text-tangerine" /> Weekly content ideas</div>
        <p className="text-ink/60 text-sm mb-4">A simple rhythm to keep your feed fresh — tick them off as you post.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {WEEK.map(([day, idea]) => (
            <label key={day} className={`brut border-ink p-3 shadow-brut cursor-pointer flex items-start gap-2 ${checked[day] ? "bg-green-100" : "bg-white"}`}>
              <input type="checkbox" checked={!!checked[day]} onChange={() => setChecked({ ...checked, [day]: !checked[day] })} className="mt-1" data-testid={`cg-week-${day}`} />
              <span><span className="font-display font-bold">{day}</span><br /><span className="text-sm text-ink/70">{idea}</span></span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}
